<template>
  <section class="preferenced-notes" :class="{'no-notes': isEditMode || zeroNotes }">
    <template>
      <div class="notes">
        <section-title>
          <template slot="title">{{ $t('PREFERENCED_NOTES_HEADER') }}</template>
          <template slot="link" v-if="!zeroNotes">
            <sh-button
              class="button-link"
              :naked="true"
              tag="a"
              :href="personalizedCategoryNotesQueryParams"
            >
              {{ $t('CONTENT_SEE_ALL_CTA') }}
            </sh-button>
          </template>
        </section-title>

        <template v-if="isEditMode || (zeroNotes && !preferencedFetching)">
          <p class="help-tip">{{ $t('CONTENT_RECOMMENDED_EMPTY_SUPPORTING') }}</p>
        </template>

        <dh-loading-indicator v-if="preferencedFetching" />
        <template v-else>
          <div
            v-if="preferencedHouseNotesCards.length > 0 && !preferencedFetching"
            class="notes-container"
          >
            <div
              v-for="note in preferencedHouseNotesCards"
              :id="note.id"
              :key="note.id"
              tabindex="0"
              class="note-item"
            >
              <nuxt-link :to="noteLink(note)" :aria-label=ariaToRead(note.name)>
                <div class="image-wrapper">
                  <img v-if="!isEditMode" class="image" v-lazy="note.images.large" :alt="note.name">
                  <img v-else class="image" :src="note.images.large" :alt="note.name">
                </div>
                <h3 class="title">{{ note.name }}</h3>
                <p class="category">{{ note.category }}</p>
              </nuxt-link>
            </div>
          </div>
        </template>

        <p class="cta-wrapper">
          <sh-button :to="customizeLink">
            {{ $t('PREFERENCED_NOTES_CUSTOMIZE_CTA') }}
          </sh-button>
        </p>
      </div>
    </template>
  </section>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import CardGroup from '~/components/card-group'
import { transformHouseNotesToCards } from '~/helpers'
import SectionTitle from '~/components/section-title'
import { NOTES_ITEM_NAME } from '~/constants/sitecore-keys'

const { mapActions: mapNoteActions, mapGetters: mapNoteGetters } = createNamespacedHelpers('content/notes')
const { mapGetters: mapAccountGetters } = createNamespacedHelpers('user/account')
const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

export default {
  name: 'PreferencedNotes',

  components: {
    CardGroup,
    SectionTitle
  },

  props: {
    rendering: {
      type: Object
    }
  },

  data () {
    return {
      notesList: []
    }
  },

  computed: {
    ...mapNoteGetters([
      'preferencedNotes',
      'preferencedFetching'
    ]),

    ...mapAccountGetters([
      'favoriteContentCategories'
    ]),

    ...mapJssGetters(['isEditMode']),

    preferencedHouseNotesCards () {
      return transformHouseNotesToCards(this.preferencedNotes, this.localePath)
    },
    personalizedCategoryNotesQueryParams () {
      const category = this.favoriteContentCategories.map(cat => cat.id).join(',')
      return this.localePath({ path: `notes?categories=${category}` })
    },
    zeroNotes () {
      return this.preferencedNotes.length === 0
    },
    customizeLink () {
      return this.localePath('settings-house-notes')
    }
  },

  mounted () {
    if (!this.isEditMode) { // not needs to load in EE mode
      this.fetchNotes()
    }
  },

  methods: {
    ...mapNoteActions([
      'fetchPreferencedNotes'
    ]),
    async fetchNotes () {
      await this.fetchPreferencedNotes()
    },
    noteLink (note) {
      return this.localePath({ path: `/${NOTES_ITEM_NAME}/${note.articleUrlSlug}` })
    },
    ariaToRead (name) {
      return this.$t('PREFERENCED_NOTE_ARIA', { name })
    }
  }
}
</script>

<style lang="scss" scoped>

.preferenced-notes {
  max-width: 1280px;
  position: relative;
  padding-left: $space-060;
  padding-right: $space-060;
  margin: auto;

  @include breakpoint(small) {
    padding: $d-med;
  }

  @include tablet {
    padding: $d-large;
  }

  @include desktop {
    margin-bottom: $space-100;
    padding: $d-xx-large;
  }

  &.no-notes {
    margin-top: 128px;
    margin-bottom: 160px;
    text-align: center;

      .section-title {
        justify-content: center;
      }
  }

  .notes {
    position: relative;
  }

  .help-tip {
    @include font('060');
    margin-bottom: $space-070;
  }

  .customize-link {
    @include deprecated-f-color-highlight;
    text-decoration: none;

    &:focus {
      border: 1px solid $deprecated-brand-secondary;
      outline: none;
    }
  }

  .notes-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: $d-large;

    @include desktop {
      padding: 0 20px;
    }
  }

  .note-item {
    margin-bottom: $space-080;
    padding: 0;
    width: 100%;

    @include tablet {
      width: calc(50% - 20px);
      margin-right: 20px;
      &:nth-child(even) {
        margin-right: 0;
      }
    }

    > a {
      text-decoration: none;
    }
  }

  .image-wrapper {
    position: relative;
    width: 100%;
    height: auto;
    overflow: hidden;
    margin-bottom: $space-070;
    text-align: justify;
    @include ratio(4, 3);
    @include desktop {
      width: 100%;
      height: 600px;
    }

    .image {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      @include lazy-load;
    }
  }

  .title {
    @include font('070');
    margin-bottom: $space-040;

    @include desktop {
      @include font('080');
    }
  }

  .button-link {
    @include font('040', 700);
  }
}

.cta-wrapper {
  width: 100%;
  text-align: center;
  margin: $space-040 0;
}
</style>
